/**
 * Shared math and copy for "hard-reseed the posterior" (`POST /markets/{key}/reseed-posterior`).
 *
 * Two places drive that endpoint — the dashboard's ManualGatingPanel and the Overview tile
 * modal — and they must agree on what a valid reseed is, or the same inputs would be accepted
 * in one place and rejected in the other. Preview/validation/confirm copy all live here.
 */

export function clampProb(p) {
  return Math.max(1e-9, Math.min(1 - 1e-9, p));
}

export function logitProb(p) {
  const pc = clampProb(p);
  return Math.log(pc / (1 - pc));
}

export function sigmoidLogit(x) {
  if (x >= 0) {
    const z = Math.exp(-x);
    return 1 / (1 + z);
  }
  const z = Math.exp(x);
  return z / (1 + z);
}

/** Largest accepted posterior variance; matches the ManualGatingPanel guard. */
export const RESEED_MAX_P = 10;

/**
 * Parse the four raw form strings into a preview.
 *
 * ``x`` wins over ``prob`` when both parse (same precedence the API applies), and whichever
 * side is missing is derived from the other so the caller always gets both.
 */
export function reseedPreview({ line, P, prob, x }) {
  const lineNum = Number.parseFloat(String(line ?? '').trim());
  const PNum = Number.parseFloat(String(P ?? '').trim());
  const pRaw = Number.parseFloat(String(prob ?? '').trim());
  const xRaw = Number.parseFloat(String(x ?? '').trim());
  const pOk = Number.isFinite(pRaw) && pRaw > 0 && pRaw < 1;
  const xOk = Number.isFinite(xRaw);
  // ``x`` is what gets submitted, so it is authoritative: derive the displayed fair from it
  // rather than echoing the prob field back. Otherwise a divergent pair (prob 0.9, x 0.1)
  // would confirm "fair 90%" and apply 52.5%.
  const xOut = xOk ? xRaw : pOk ? logitProb(pRaw) : null;
  const pOut = xOk ? sigmoidLogit(xRaw) : pOk ? pRaw : null;
  const sqrtP = Number.isFinite(PNum) && PNum > 0 ? Math.sqrt(PNum) : null;
  const sdProb =
    xOut != null && sqrtP != null
      ? { lo: sigmoidLogit(xOut - 1.64 * sqrtP), hi: sigmoidLogit(xOut + 1.64 * sqrtP) }
      : null;
  return {
    line: Number.isFinite(lineNum) ? lineNum : null,
    P: Number.isFinite(PNum) && PNum > 0 ? PNum : null,
    x: xOut,
    p: pOut,
    sqrtP,
    sdProb,
  };
}

/** First problem with a preview, or null when it is submittable. */
export function reseedValidationError(preview) {
  if (!preview) return 'Enter a finite baseline line.';
  if (preview.line == null) return 'Enter a finite baseline line.';
  if (preview.P == null || preview.P > RESEED_MAX_P) {
    return `Enter P in (0, ${RESEED_MAX_P}] (logit variance).`;
  }
  if (preview.x == null || preview.p == null) {
    return 'Enter fair as probability in (0,1) or logit x.';
  }
  return null;
}

/** Confirmation body text — states everything the reseed will overwrite. */
export function reseedConfirmBody(preview, { lockBaseline = true } = {}) {
  const { line, p, x, P, sqrtP } = preview;
  return (
    `Hard-reseed line=${line}, fair=${(p * 100).toFixed(1)}% (x=${x.toFixed(4)}), P=${P.toFixed(4)}` +
    (sqrtP != null ? ` (√P=${sqrtP.toFixed(3)})` : '') +
    `. Clears jump excess; full OB rebuild.` +
    (lockBaseline ? ' Locks KF baseline to this line.' : '')
  );
}

/**
 * Form values seeded from the market's current quote.
 *
 * Returns null when the quote carries none of line/fair/x/P, so a caller can leave the form
 * untouched rather than blanking it against an empty quote.
 */
export function reseedInputsFromQuote(quote) {
  if (!quote) return null;
  const L = quote?.line;
  const pf = quote?.p_fair;
  const qx = quote?.x;
  const qP = quote?.P;
  if (pf == null && qx == null && qP == null && L == null) return null;

  let probInput = '';
  let xInput = '';
  if (pf != null && Number.isFinite(Number(pf))) {
    const p = Number(pf);
    probInput = p.toFixed(4);
    xInput =
      qx != null && Number.isFinite(Number(qx)) ? Number(qx).toFixed(4) : logitProb(p).toFixed(4);
  } else if (qx != null && Number.isFinite(Number(qx))) {
    const xv = Number(qx);
    xInput = xv.toFixed(4);
    probInput = sigmoidLogit(xv).toFixed(4);
  }
  return {
    line: L != null && Number.isFinite(Number(L)) ? String(L) : '',
    prob: probInput,
    x: xInput,
    P: qP != null && Number.isFinite(Number(qP)) ? Number(qP).toFixed(4) : '',
  };
}

/** Follow-up warning when the server reseeded but √P still exceeds the gate. */
export function reseedSqrtPGateWarning(res) {
  if (!res?.would_fail_sqrt_p_gate) return null;
  return `P=${Number(res.P).toFixed(4)}, √P=${Number(res.sqrt_P).toFixed(3)} > max ${Number(
    res.max_sqrt_p
  ).toFixed(3)}. Quote may stay gated until P falls.`;
}

/** Best available human-readable message from a failed reseed request. */
export function reseedErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  return (
    (typeof detail === 'object' && detail?.message) ||
    (typeof detail === 'string' ? detail : null) ||
    err?.message ||
    'Could not reseed.'
  );
}
